import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

// Plain-language operator guide. Audience is the site owner with no web
// or coding background. Anything that genuinely needs technical
// intervention is signposted with "ask Luca" rather than partial
// instructions that would just confuse.

const SECTIONS = [
  { id: "what-it-is",      title: "What this site is" },
  { id: "signing-in",      title: "Signing in" },
  { id: "the-editor",      title: "The editor — what's where" },
  { id: "changing-text",   title: "Changing text" },
  { id: "changing-images", title: "Changing images and videos" },
  { id: "showing-hiding",  title: "Showing and hiding works" },
  { id: "layout-editor",   title: "Customising tile and image layout" },
  { id: "external-links",  title: "External links and the showreel" },
  { id: "file-types",      title: "What file types work" },
  { id: "size-limits",     title: "Size limits and big videos" },
  { id: "saving",          title: "Saving your changes" },
  { id: "going-live",      title: "How long until it's live" },
  { id: "revisions",       title: "Going back to a previous version" },
  { id: "common-fixes",    title: "Common situations and fixes" },
  { id: "before-luca",     title: "Before asking Luca — quick checks" },
  { id: "ask-luca",        title: "When to ask Luca" },
];

export default function AdminGuide() {
  const navigate = useNavigate();
  const [active, setActive] = useState(SECTIONS[0].id);

  useEffect(() => {
    document.title = "Guide · Ruby Fos";
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActive(entry.target.id);
        }
      },
      { rootMargin: "-30% 0px -60% 0px" },
    );
    for (const s of SECTIONS) {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, []);

  async function logout() {
    await fetch("/api/admin/logout", { method: "POST", credentials: "same-origin" });
    navigate("/admin/login", { replace: true });
  }

  return (
    <div style={{ minHeight: "100vh", background: "#0c0c0c", color: "#f4ece0", fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <header style={{ padding: "1.2rem 1.5rem", borderBottom: "1px solid #1a1a1a", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "rgba(12,12,12,0.94)", backdropFilter: "blur(8px)", zIndex: 10 }}>
        <div style={{ display: "flex", gap: "1rem", alignItems: "baseline" }}>
          <Link to="/admin" style={{ color: "#888", textDecoration: "none", fontSize: "0.85rem" }}>← admin</Link>
          <div style={{ fontSize: "1.05rem", letterSpacing: "0.04em" }}>Guide</div>
        </div>
        <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          <Link to="/admin/edit" style={{ color: "#888", textDecoration: "none", fontSize: "0.85rem" }}>editor</Link>
          <Link to="/" target="_blank" rel="noopener" style={{ color: "#888", textDecoration: "none", fontSize: "0.85rem" }}>view site ↗</Link>
          <button type="button" onClick={logout} style={ghostBtn}>sign out</button>
        </div>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(200px, 240px) minmax(0, 1fr)", gap: "2.5rem", maxWidth: 1100, margin: "0 auto", padding: "2rem 1.5rem 6rem" }} className="guide-grid">
        <aside style={{ position: "sticky", top: "5.5rem", alignSelf: "start", maxHeight: "calc(100vh - 7rem)", overflowY: "auto" }}>
          <div style={{ fontSize: "0.72rem", color: "#666", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "0.8rem" }}>On this page</div>
          <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.45rem", fontSize: "0.85rem" }}>
            {SECTIONS.map((s, i) => (
              <li key={s.id}>
                <a href={`#${s.id}`} style={{ color: active === s.id ? "#f4ece0" : "#888", textDecoration: "none", borderLeft: `2px solid ${active === s.id ? "#f4ece0" : "transparent"}`, paddingLeft: "0.7rem", display: "block", transition: "color 120ms, border-color 120ms" }}>
                  <span style={{ color: "#555", marginRight: "0.5rem" }}>{String(i + 1).padStart(2, "0")}</span>
                  {s.title}
                </a>
              </li>
            ))}
          </ol>
        </aside>

        <article style={{ minWidth: 0 }}>

          {/* ── 01 ───────────────────────────────────────────────── */}
          <Section id="what-it-is" title="What this site is">
            <p>
              This is Ruby Fos's portfolio website, at{" "}
              <strong>rubys-website.pages.dev</strong>. The admin editor lets
              you change a wide range of content directly — no coding needed.
            </p>
            <p>What you can change from the editor:</p>
            <ul>
              <li>
                <strong>Home</strong> — the large title text and background photo.
              </li>
              <li>
                <strong>Works intro</strong> — the autoplay reel video, and the
                label and destination of the link that takes visitors to the gallery.
              </li>
              <li>
                <strong>Works gallery</strong> — the background image, an optional
                film-grain overlay, each work tile's position on the page, and
                a configurable bottom margin that applies to both the gallery
                and every individual work page.
              </li>
              <li>
                <strong>Each work</strong> — title, year, role, description, cover
                image, any number of gallery stills, an optional external link
                (e.g. a trailer or IMDb page), whether it's visible on the gallery
                page, and the position of each gallery still on the work's page.
              </li>
              <li>
                <strong>Contact</strong> — tagline, showreel link (label and URL),
                email, phone, and background image.
              </li>
            </ul>
            <p>Things that need Luca (not doable from the editor):</p>
            <ul>
              <li>layout, colours, fonts, and animations;</li>
              <li>adding or permanently deleting a work;</li>
              <li>adding new sections or pages;</li>
              <li>domain, hosting, or anything with a "Cloudflare" error.</li>
            </ul>
          </Section>

          {/* ── 02 ───────────────────────────────────────────────── */}
          <Section id="signing-in" title="Signing in">
            <p>
              Go to <code>rubys-website.pages.dev/admin/login</code>. Type the
              password and click <strong>Sign in</strong>. You stay signed in for
              seven days, even after closing the browser.
            </p>
            <Tip>Forgot the password? Ask Luca — he can change it in about a minute.</Tip>
          </Section>

          {/* ── 03 ───────────────────────────────────────────────── */}
          <Section id="the-editor" title="The editor — what's where">
            <p>
              The editor is one long scrollable page, divided into sections with
              grey headings:
            </p>
            <ul>
              <li>
                <strong>Home (hero)</strong> — title text and background photo.
              </li>
              <li>
                <strong>Works intro</strong> — the autoplay reel video, plus
                two fields to edit the label and destination of the "DEEPER" link.
              </li>
              <li>
                <strong>Works gallery (/works/deeper)</strong> — background image,
                a checkbox for the film-grain overlay (off by default), and a{" "}
                <em>Customize tile layout…</em> button (see section 07).
              </li>
              <li>
                <strong>Gallery pages — bottom margin</strong> — a number field
                (in inches, default 0.5) that controls the empty space at the
                very bottom of the gallery page and every individual work page.
                Increase it if the last image sits too close to the edge of
                the screen; set it to 0 to remove the gap entirely.
              </li>
              <li>
                <strong>Contact</strong> — tagline; showreel label and URL (the bold
                link that appears between the tagline and contact info on the contact
                page — see section 08); email; phone; background image.
              </li>
              <li>
                <strong>Works</strong> — each collapsed by default, click the title
                bar to expand. Inside each work:
                <ul style={{ marginTop: "0.4rem" }}>
                  <li>
                    <em>Visible on gallery</em> — checkbox to show or hide the tile
                    on the gallery page (section 06);
                  </li>
                  <li>title, year, role, description;</li>
                  <li>
                    <em>External link label</em> and <em>External link URL</em> — an
                    optional link at the bottom of the work's page (section 08);
                  </li>
                  <li>cover image;</li>
                  <li>
                    gallery stills — any number, with + to add and × to remove;
                  </li>
                  <li>
                    <em>Customize tile layout…</em> and <em>Customize layout…</em>{" "}
                    buttons for the visual position editors (section 07).
                  </li>
                </ul>
              </li>
            </ul>
            <p>
              The sticky bar at the bottom of the screen holds the{" "}
              <strong>Save</strong> button and a timestamp of the last save. The
              button only lights up when something has actually changed.
            </p>
          </Section>

          {/* ── 04 ───────────────────────────────────────────────── */}
          <Section id="changing-text" title="Changing text">
            <p>
              Click any text field, type, and the change is held in the editor
              (not yet live). Short single-line inputs (titles, year, email) and
              larger multi-line boxes (descriptions, tagline) work the same way.
            </p>
            <p>
              The contact <em>email</em> and <em>phone</em> fields show a small
              orange warning if the value looks wrong — e.g. an email missing "@",
              or a phone with no digits. You can still save, but the click-to-email
              and click-to-call links won't work until the value is corrected.
            </p>
            <p>
              The <em>External link label</em> and <em>URL</em> fields (per-work
              and contact showreel) work identically — click, type, done. Leave
              both fields empty to hide the link entirely.
            </p>
          </Section>

          {/* ── 05 ───────────────────────────────────────────────── */}
          <Section id="changing-images" title="Changing images and videos">
            <p>
              Each image or video field has a <strong>drop zone</strong> — a
              dashed rectangle with a thumbnail of the current file. Two ways to
              replace it:
            </p>
            <ol>
              <li><strong>Drag and drop</strong> a file from Finder onto the rectangle.</li>
              <li><strong>Click the rectangle</strong> and pick a file from the dialog.</li>
            </ol>
            <p>
              Upload starts immediately. You'll see <em>uploading… 47%</em> and a
              progress line. When done, the caption updates. The label inside the
              drop zone tells you which file types it accepts.
            </p>
            <Tip>
              Uploading doesn't save. Click <strong>Save</strong> after uploading
              for changes to appear on the public site.
            </Tip>
            <p style={{ marginTop: "1.1rem" }}>
              <strong>Adding or removing gallery stills</strong>
            </p>
            <p>
              Each work can have any number of gallery stills. To add one:
            </p>
            <ol>
              <li>Expand the work.</li>
              <li>Click <strong>+ Add still</strong> below the last slot.</li>
              <li>Upload to the new drop zone.</li>
              <li>Click <strong>Save</strong>.</li>
            </ol>
            <p>
              To remove a still, click <strong>×</strong> next to its drop zone.
              The slot disappears in the editor immediately but isn't removed from
              the public site until you save. Every work needs at least one still —
              the × is disabled on the last remaining slot.
            </p>
          </Section>

          {/* ── 06 ───────────────────────────────────────────────── */}
          <Section id="showing-hiding" title="Showing and hiding works">
            <p>
              Each work has a <strong>Visible on gallery</strong> checkbox at the
              top of its editor panel.
            </p>
            <ul>
              <li>
                <strong>Unchecked (hidden)</strong> — the tile disappears from the
                gallery and the work's URL redirects back to the gallery. All
                content and settings are preserved; it's just invisible to visitors.
              </li>
              <li>
                <strong>Checked (visible)</strong> — appears on the gallery as normal.
              </li>
            </ul>
            <p>
              Remaining tiles rearrange automatically when one is hidden. Re-checking
              brings it back.
            </p>
            <Tip>
              After saving, hard-refresh the gallery page to see the change — see
              section 15 for how.
            </Tip>
            <p>To permanently delete a work entirely, ask Luca.</p>
          </Section>

          {/* ── 07 ───────────────────────────────────────────────── */}
          <Section id="layout-editor" title="Customising tile and image layout">
            <p>
              Two visual editors let you drag and resize content without touching
              any code.
            </p>
            <p><strong>Tile layout — the gallery page (/works/deeper)</strong></p>
            <ol>
              <li>Scroll to the <em>Works gallery</em> section in the editor.</li>
              <li>Click <strong>Customize tile layout…</strong>.</li>
              <li>
                A wireframe appears with each visible work tile. <strong>Drag</strong>{" "}
                to move, <strong>drag the orange corner</strong> to resize the width.
              </li>
              <li>Click <strong>Apply</strong> to keep, or <strong>Cancel</strong> to discard.</li>
              <li>
                <strong>Reset to auto-layout</strong> clears all overrides and
                returns tiles to the automatic three-column grid.
              </li>
              <li>Click <strong>Save</strong> on the main page to publish.</li>
            </ol>
            <p><strong>Gallery image layout — a work's detail page</strong></p>
            <ol>
              <li>Expand a work, scroll past the gallery stills.</li>
              <li>Click <strong>Customize layout…</strong>.</li>
              <li>
                A wireframe of that work's page appears — each rectangle is a
                gallery still, the purple block is the description text.
              </li>
              <li>Drag and resize, then Apply → Save.</li>
              <li>
                <strong>Reset to auto-layout</strong> returns images to the
                automatic two-column arrangement and moves the description block
                to sit just below the last image — use this if the description
                is overlapping a photo.
              </li>
            </ol>
            <Tip>
              The wireframe reflects your current screen's proportions, so what
              you see in the editor matches what visitors see at the same browser
              size. On a very different screen (e.g. phone vs. widescreen monitor)
              the exact spacing will vary slightly — hard-refresh the public page
              after saving to confirm (section 15).
            </Tip>
          </Section>

          {/* ── 08 ───────────────────────────────────────────────── */}
          <Section id="external-links" title="External links and the showreel">
            <p>
              There are two types of external link you can manage from the editor.
              Both work the same way: fill in a label (the text people see) and a
              URL (the address it goes to). Leaving both fields blank hides the
              link entirely.
            </p>

            <p><strong>Per-work external link</strong></p>
            <p>
              Each work can show a large link at the bottom of its page — for
              example a trailer, IMDb listing, or festival page. Visitors who move
              their cursor toward the bottom of the screen see it fade in; clicking
              opens it in a new tab.
            </p>
            <ol>
              <li>Expand the work in the editor.</li>
              <li>
                Fill in <em>External link label</em> (e.g.{" "}
                <code>Watch on IMDb</code>) and <em>External link URL</em> (e.g.{" "}
                <code>https://www.imdb.com/title/tt…</code>).
              </li>
              <li>Click <strong>Save</strong>.</li>
            </ol>

            <p><strong>Contact page showreel link</strong></p>
            <p>
              The contact page can show a bold link between the tagline and the
              email/phone block — the showreel. It appears with the same
              hover-underline style as the contact details but in bold.
            </p>
            <ol>
              <li>Scroll to the <em>Contact</em> section in the editor.</li>
              <li>
                Fill in <em>Showreel label</em> (e.g. <code>Showreel</code>) and{" "}
                <em>Showreel URL</em> (e.g. <code>https://vimeo.com/…</code>).
              </li>
              <li>Click <strong>Save</strong>.</li>
            </ol>
            <Tip>
              Both the per-work external link and the showreel open in a new tab —
              they go to outside websites, not other pages within Ruby's site.
            </Tip>
          </Section>

          {/* ── 09 ───────────────────────────────────────────────── */}
          <Section id="file-types" title="What file types work">
            <p>
              <strong>Images</strong> (hero, contact, works gallery backdrop,
              covers, gallery stills): <code>jpg</code>, <code>jpeg</code>,{" "}
              <code>png</code>, <code>webp</code>, <code>gif</code>.
            </p>
            <p>
              <strong>Video</strong> (reel only): <code>mp4</code>,{" "}
              <code>mov</code>, <code>webm</code>.
            </p>
            <p>
              Anything else — <code>HEIC</code>, <code>SVG</code>,{" "}
              <code>AVI</code>, <code>TIFF</code>, etc. — won't upload. The drop
              zone shows "error: 415" instead of progressing.
            </p>
            <Tip>
              <strong>iPhone photos are HEIC by default.</strong> To convert: open
              the photo, share to Mail (auto-converts), or open in Preview on Mac
              → <em>File → Export…</em> → choose JPEG.
            </Tip>
          </Section>

          {/* ── 10 ───────────────────────────────────────────────── */}
          <Section id="size-limits" title="Size limits and big videos">
            <p>
              The editor accepts files up to about <strong>95 MB</strong>
              comfortably. Above that a pop-up appears before upload starts. Most
              photos are 2–10 MB; video reels can easily exceed 100 MB.
            </p>
            <p>
              <strong>If you hit the size warning</strong>: click "Open R2 in CF
              dashboard ↗" in the pop-up to upload directly to the right storage
              folder (the pop-up names it). The site picks it up within a minute.
              If the dashboard looks confusing, ask Luca — he can do it in about
              thirty seconds.
            </p>
          </Section>

          {/* ── 11 ───────────────────────────────────────────────── */}
          <Section id="saving" title="Saving your changes">
            <p>
              The <strong>Save</strong> button at the bottom-right of the editor
              turns from grey to cream when you've changed something. Click it.
              It briefly says "saving…" then "Saved", and the timestamp updates
              to <em>saved · &lt;date and time&gt;</em>.
            </p>
            <p>
              Leaving the page with unsaved changes triggers a browser
              confirmation. Clicking <strong>sign out</strong> with unsaved changes
              shows a confirm dialog so nothing is lost by accident.
            </p>
          </Section>

          {/* ── 12 ───────────────────────────────────────────────── */}
          <Section id="going-live" title="How long until it's live">
            <p>
              <strong>Text changes</strong> — instant. The next page load shows
              the new content.
            </p>
            <p>
              <strong>Image and video changes</strong> — up to one minute, due to
              caching. To verify, hard-refresh the public site after saving:
            </p>
            <ul>
              <li>
                <strong>Mac</strong>: <kbd>⌘</kbd> + <kbd>Shift</kbd> + <kbd>R</kbd>
              </li>
              <li>
                <strong>Windows</strong>: <kbd>Ctrl</kbd> + <kbd>Shift</kbd> +{" "}
                <kbd>R</kbd> or <kbd>Ctrl</kbd> + <kbd>F5</kbd>
              </li>
            </ul>
            <p>Section 15 has hard-refresh steps for iPhone, iPad, and Android.</p>
          </Section>

          {/* ── 13 ───────────────────────────────────────────────── */}
          <Section id="revisions" title="Going back to a previous version">
            <p>
              Every Save creates a snapshot of the previous state. The last{" "}
              <strong>10</strong> snapshots are kept; older ones are removed
              automatically.
            </p>
            <p>
              Click <strong>revisions</strong> in the editor header (or visit{" "}
              <code>/admin/revisions</code>) to see a list of saves with dates.
              Click <strong>restore</strong> next to any entry to roll back.
            </p>
            <p>
              The current state is always snapshotted before the restore, so
              restoring is itself reversible.
            </p>
          </Section>

          {/* ── 14 ───────────────────────────────────────────────── */}
          <Section id="common-fixes" title="Common situations and fixes">
            <FaqRow
              q="I saved but the site still shows the old content."
              a={<>Hard-refresh the public site — not the admin page. See section 15 for the exact steps on your device. Image and video changes can take up to one minute; text is usually instant.</>}
            />
            <FaqRow
              q="I hid a work but it's still showing on the gallery."
              a={<>Hard-refresh the gallery page (/works/deeper). If it still appears after a minute, confirm the work's <em>Visible on gallery</em> checkbox is unchecked and that Save shows "Saved" with today's time.</>}
            />
            <FaqRow
              q="I changed something but can't see it on the public site."
              a={<>Wait one minute, then hard-refresh (section 15). Still not there? Check the editor — the Save button should say "Saved" and the timestamp should match when you clicked it.</>}
            />
            <FaqRow
              q="The Save button isn't lighting up even though I made a change."
              a={<>You probably typed something and changed it back to exactly what was there before. The editor only marks itself as changed when the content is genuinely different.</>}
            />
            <FaqRow
              q="I uploaded the wrong photo."
              a={<>Just upload the right one. The editor always uses the most recently uploaded file for each slot. The wrong upload stays in storage as history but isn't shown publicly.</>}
            />
            <FaqRow
              q="I deleted text and now a field is blank."
              a={<>Critical fields (title, email, etc.) can't be saved empty. The save will be rejected with an error message. Type something back in and save.</>}
            />
            <FaqRow
              q="A pop-up says my file is too large."
              a={<>Use the "Open R2 in CF dashboard" button in the pop-up (section 10). If confusing, ask Luca.</>}
            />
            <FaqRow
              q="It says I'm signed out / 'unauthorized' / asks me to sign in again."
              a={<>Sessions last 7 days. If yours expired mid-edit, sign back in — the editor remembers what you were typing. Click Save to publish your edits.</>}
            />
            <FaqRow
              q="I want to undo the last save."
              a={<>Open <strong>revisions</strong>, find the previous save by date, and restore it. The current state is snapshotted first so you can reverse again if needed.</>}
            />
            <FaqRow
              q="The tile or gallery layout looks wrong after customising."
              a={<>Click <strong>reset to auto-layout</strong> inside the layout editor (Customize tile layout… or Customize layout…), then Apply and Save. Hard-refresh the public site after. If still wrong, ask Luca.</>}
            />
            <FaqRow
              q="There's too much (or too little) space at the bottom of the gallery or work pages."
              a={<>Scroll to <strong>Gallery pages — bottom margin</strong> in the editor (between the Works gallery backdrop section and Contact). Change the number — it's in inches, default 0.5. Click Save. The change applies to both the /works/deeper gallery page and every individual work page.</>}
            />
            <FaqRow
              q="The description text is overlapping photos on a work's page."
              a={<>Open that work's layout editor (Customize layout…) and click <strong>reset to auto-layout</strong>. This automatically places the description below all photos. Click Apply, then Save.</>}
            />
            <FaqRow
              q="The site looks broken / white screen / pages won't load."
              a={<>Hard-refresh first (section 15). If that doesn't fix it, ask Luca — broken layout is a code issue, not a content issue.</>}
            />
            <FaqRow
              q="My iPhone photo won't upload (file type error)."
              a={<>iPhones save as HEIC by default, which isn't supported. Open in Preview on Mac → <em>File → Export…</em> → JPEG, then upload. Or share via Mail — it converts automatically.</>}
            />
          </Section>

          {/* ── 15 ───────────────────────────────────────────────── */}
          <Section id="before-luca" title="Before asking Luca — quick checks">
            <p>Most issues are solved by one of these two steps. Try them first.</p>
            <div style={{ background: "#0e1a10", borderLeft: "2px solid #5a9e6a", borderRadius: "0 4px 4px 0", padding: "1rem 1.2rem", margin: "0.2rem 0 1.2rem" }}>
              <p style={{ margin: "0 0 0.5rem", color: "#8ecc9a", fontWeight: 500 }}>① Hard-refresh the public site</p>
              <p style={{ margin: "0 0 0.7rem" }}>
                Browsers cache the site aggressively. After saving, you and visitors
                may still see the old version until you force a full reload. This is
                different from a normal refresh:
              </p>
              <ul style={{ margin: "0 0 0.7rem" }}>
                <li>
                  <strong>Mac</strong>: <kbd>⌘</kbd> + <kbd>Shift</kbd> + <kbd>R</kbd>
                </li>
                <li>
                  <strong>Windows</strong>: <kbd>Ctrl</kbd> + <kbd>Shift</kbd> +{" "}
                  <kbd>R</kbd> or <kbd>Ctrl</kbd> + <kbd>F5</kbd>
                </li>
                <li>
                  <strong>iPhone / iPad (Safari)</strong>: hold the ↺ reload button
                  until a menu appears → tap <em>Reload Without Content Blockers</em>.
                  If that option isn't there: Settings → Safari → Clear History and
                  Website Data, then reload.
                </li>
                <li>
                  <strong>Android (Chrome)</strong>: tap the three-dot menu →
                  tap the ↺ reload icon in the address bar.
                </li>
              </ul>
              <p style={{ margin: 0, color: "#aaa", fontSize: "0.88rem" }}>
                Do this on the public site (<code>rubys-website.pages.dev</code>), not
                the admin panel. Image and video changes can take up to one minute
                even after a hard-refresh — that's normal.
              </p>
            </div>
            <div style={{ background: "#0e1a10", borderLeft: "2px solid #5a9e6a", borderRadius: "0 4px 4px 0", padding: "1rem 1.2rem", margin: "0.2rem 0 1.2rem" }}>
              <p style={{ margin: "0 0 0.5rem", color: "#8ecc9a", fontWeight: 500 }}>② Check the Save timestamp</p>
              <p style={{ margin: 0 }}>
                The bottom-right of the editor always shows when content was last
                saved (e.g. <em>saved · 6 May 2026, 14:32</em>). If the timestamp
                matches when you made your change, the content is saved — it's a
                browser cache issue, so hard-refresh (step ① above). If the
                timestamp is old, the save didn't land — click Save again.
              </p>
            </div>
            <p>If both steps don't help, then ask Luca.</p>
          </Section>

          {/* ── 16 ───────────────────────────────────────────────── */}
          <Section id="ask-luca" title="When to ask Luca">
            <p>
              The editor covers a lot — text, photos, videos, showing and hiding
              works, customising layouts, external links, and restoring previous
              versions. The things below genuinely can't be done from the editor
              and need a code change:
            </p>
            <ul>
              <li>changing layout, colours, fonts, or animations;</li>
              <li>
                adding a brand new work entry or permanently deleting one — hiding
                and showing existing works is self-serve (section 06);
              </li>
              <li>adding new pages or sections to the site;</li>
              <li>connecting a custom domain (e.g. rubyfos.com);</li>
              <li>
                any error message that uses the words "schema", "build", "deploy",
                "wrangler", or "Cloudflare" — screenshot it and send it over;
              </li>
              <li>
                if hard-refreshing (section 15) doesn't fix something visibly broken
                on the public site;
              </li>
              <li>
                if the editor and public site keep showing different content even
                after saving again and hard-refreshing.
              </li>
            </ul>
            <p style={{ color: "#888" }}>
              Screenshots help. The exact text of any error message helps even more.
            </p>
          </Section>

        </article>
      </div>

      <style>{`
        @media (max-width: 760px) {
          .guide-grid { grid-template-columns: 1fr !important; }
          .guide-grid > aside { position: static !important; max-height: none !important; }
        }
      `}</style>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} style={{ scrollMarginTop: "5.5rem", marginBottom: "2.6rem" }}>
      <h2 style={{ fontSize: "1.15rem", fontWeight: 500, letterSpacing: "0.03em", margin: "0 0 0.9rem 0", paddingBottom: "0.5rem", borderBottom: "1px solid #1a1a1a" }}>
        {title}
      </h2>
      <div style={{ color: "#cfcabd", fontSize: "0.95rem", lineHeight: 1.65 }} className="guide-prose">
        {children}
      </div>
      <style>{`
        .guide-prose p { margin: 0 0 0.9rem 0; }
        .guide-prose ul, .guide-prose ol { margin: 0 0 0.9rem 0; padding-left: 1.4rem; }
        .guide-prose li { margin-bottom: 0.35rem; }
        .guide-prose code { background: #1a1a1a; color: #f4ece0; padding: 0.08rem 0.32rem; border-radius: 3px; font-size: 0.86em; font-family: ui-monospace, monospace; }
        .guide-prose kbd { background: #1a1a1a; border: 1px solid #2a2a2a; color: #f4ece0; padding: 0.05rem 0.32rem; border-radius: 3px; font-size: 0.78em; font-family: ui-monospace, monospace; }
        .guide-prose strong { color: #f4ece0; }
      `}</style>
    </section>
  );
}

function Tip({ children }) {
  return (
    <div style={{ background: "#101418", borderLeft: "2px solid #6aa0c8", padding: "0.7rem 1rem", margin: "0.4rem 0 1rem", color: "#cfcabd", fontSize: "0.88rem", borderRadius: "0 3px 3px 0" }}>
      <strong style={{ color: "#9ec5e3", marginRight: "0.4rem" }}>Tip</strong>
      {children}
    </div>
  );
}

function FaqRow({ q, a }) {
  return (
    <div style={{ padding: "0.9rem 0", borderBottom: "1px solid #1a1a1a" }}>
      <div style={{ color: "#f4ece0", fontWeight: 500, fontSize: "0.95rem", marginBottom: "0.4rem" }}>{q}</div>
      <div style={{ color: "#aaa", fontSize: "0.9rem", lineHeight: 1.6 }}>{a}</div>
    </div>
  );
}

const ghostBtn = {
  background: "transparent", color: "#aaa", border: "none",
  padding: "0.3rem 0.5rem", fontSize: "0.85rem", cursor: "pointer",
};
